import React, { useMemo } from 'react';
import { Box, Skeleton } from '@mui/material';
import { Text, TextView, ChartTooltip } from '../../../../../../atoms';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { DateTime } from 'luxon';

interface callGetSrtTokenUsageProps {
  usageData?: SrtToken[];
  dates: { start: DateTime; end: DateTime };
  loading?: boolean;
}

interface DayBucket {
  date: string;
  response: number;
  sentiment: number;
  enquiries: number;
  whatsapp: number;
  twilio: number;
  negotiator: number;
  other: number;
  total: number;
}

/** `markupType` values that make up the Twilio bill. */
const TWILIO_MARKUPS = new Set([
  'inbound_call',
  'outbound_call',
  'inbound_sms',
  'outbound_sms',
  'twilio_other'
]);

type TooltipPayloadEntry = { value?: number; dataKey?: string; color?: string };

// Stacked segments, in stack order. Every channel we bill for has its own
// segment; `other` is the catch-all that keeps the bar total equal to overall
// usage, so the stack stays consistent with "Usage this month".
//
// Previously only the three AI sub-types were broken out and everything else
// — WhatsApp, Twilio and Negotiator — fell into `other`, which is why the
// chart was mostly one grey block and the tiles explained a small fraction of
// the bill. A client asking "what am I paying for?" got no answer.
const SEGMENTS: { key: keyof Omit<DayBucket, 'date' | 'total'>; label: string; color: string }[] = [
  { key: 'response', label: 'Response', color: '#086BDE' },
  { key: 'sentiment', label: 'Sentiment', color: '#27AE60' },
  { key: 'enquiries', label: 'Enquiries', color: '#F2994A' },
  { key: 'whatsapp', label: 'WhatsApp', color: '#128C7E' },
  { key: 'twilio', label: 'Twilio', color: '#F22F46' },
  { key: 'negotiator', label: 'Negotiator', color: '#7C3AED' },
  { key: 'other', label: 'Other', color: '#BDBDBD' }
];

const SrtUsageChart: React.FC<callGetSrtTokenUsageProps> = ({
  usageData,
  dates,
  loading
}: callGetSrtTokenUsageProps) => {
  // Skeleton only on first load / month switch (no data yet) — not on every
  // background refetch, so the chart doesn't flicker when the tab regains focus.
  const showSkeleton = !!loading && !usageData;

  // Derived state — computed during render via useMemo instead of via a
  // useEffect→setState pair, so the chart paints in one pass.
  const values = useMemo<DayBucket[]>(() => {
    if (!usageData) return [];

    const lastDay = dates.start.daysInMonth ?? 0;
    const byDay = new Map<number, DayBucket>();

    let day = dates.start;
    for (let i = 1; i <= lastDay; i++) {
      byDay.set(i, {
        date: day.toISO() ?? '',
        response: 0,
        sentiment: 0,
        enquiries: 0,
        whatsapp: 0,
        twilio: 0,
        negotiator: 0,
        other: 0,
        total: 0
      });
      day = day.plus({ day: 1 });
    }

    usageData.forEach((item) => {
      if (!item.date) return;
      const bucket = byDay.get(DateTime.fromISO(item.date).day);
      if (!bucket) return;

      const tokens = Number(item.srtToken) || 0;
      const markup = (item.markupType ?? '').toLowerCase();

      // `ai_processing%` is matched by prefix (not an exact list) so a new AI
      // tier is billed under AI automatically — same rule the server uses.
      if (markup.startsWith('ai_processing')) {
        const aiType = (item.aiType ?? '').toLowerCase();
        if (aiType === 'response') bucket.response += tokens;
        else if (aiType === 'sentiment') bucket.sentiment += tokens;
        else if (aiType === 'enquiries') bucket.enquiries += tokens;
        else bucket.other += tokens;
      } else if (markup === 'whatsapp') {
        bucket.whatsapp += tokens;
      } else if (TWILIO_MARKUPS.has(markup)) {
        bucket.twilio += tokens;
      } else if (markup === 'reapit') {
        // "Negotiator" is what the client knows it as; `reapit` is the
        // internal markup type.
        bucket.negotiator += tokens;
      } else {
        bucket.other += tokens;
      }
      bucket.total += tokens;
    });

    return Array.from(byDay.values());
  }, [usageData, dates.start]);

  // Keyed by segment so the tiles below can't drift from the chart: adding a
  // segment adds its tile, and the tiles always sum to the stack.
  const totals = useMemo(() => {
    const acc: Record<string, number> = {};
    SEGMENTS.forEach((segment) => {
      acc[segment.key] = values.reduce((sum, v) => sum + v[segment.key], 0);
    });
    return acc;
  }, [values]);

  // Only channels the account was actually charged for. A tile (and its bar,
  // legend entry and colour) reading 0.00 is noise on a billing page — most
  // accounts use a subset of AI / WhatsApp / Twilio / Negotiator, and `other`
  // should sit at zero permanently now that every markupType has a home.
  // Deriving the chart from the same list keeps the legend honest: a channel
  // with no usage doesn't appear anywhere.
  const visibleSegments = useMemo(
    () =>
      // Filter on the DISPLAYED value, not the raw one. Tiles render
      // `.toFixed(2)`, so a total of e.g. 0.004 is > 0 yet still shows as
      // "0.00" — hiding it only when it rounds to zero is what makes the rule
      // "no card reads 0.00" actually hold.
      SEGMENTS.filter((segment) => Number((totals[segment.key] ?? 0).toFixed(2)) > 0),
    [totals]
  );

  interface CustomizedAxisTickProps {
    x: number;
    y: number;
    payload: { value: any };
  }

  const CustomizedAxisTick = (props: CustomizedAxisTickProps) => {
    const { x, y, payload } = props;

    return (
      <text
        x={x}
        y={y}
        dx={16}
        dy={10}
        textAnchor="end"
        fill="#666"
        style={{ fontFamily: 'Montserrat', fontSize: 10, fontWeight: 400 }}
      >
        {DateTime.fromISO(payload.value).toFormat('dd LLL')}
      </text>
    );
  };

  const CustomizedYAxisTick = (props: CustomizedAxisTickProps) => {
    const { x, y, payload } = props;

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={4}
          textAnchor="end"
          fill="#666"
          style={{ fontFamily: 'Montserrat', fontSize: 12, fontWeight: 400 }}
        >
          {Number(payload.value)}
        </text>
      </g>
    );
  };

  const nameMap = useMemo(() => Object.fromEntries(SEGMENTS.map((s) => [s.key, s.label])), []);

  // Total sits on top of the whole stack — rendered via the topmost (last) segment.
  const renderTotalLabel = ({
    x,
    y,
    width,
    index
  }: {
    x: number;
    y: number;
    width: number;
    index: number;
  }) => {
    const total = values[index]?.total ?? 0;
    if (!total) return <></>;
    return (
      <text x={x + width / 2} y={y} fill="#666" fontSize={10} textAnchor="middle" dy={-6}>
        {total.toFixed(2)}
      </text>
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: 'rgba(36, 36, 35, 0.08)',
        pt: 2,
        borderRadius: 1,
        pb: 2
        // No fixed height: the explanatory note under the tiles wraps to a
        // different number of lines by viewport width, and the tile row itself
        // wraps once an account has several channels. A fixed 460 with the
        // chart at 70% of it meant either could push the chart out of the box.
        // The chart carries its own height instead, so this grows to fit.
      }}
    >
      <TextView fontSize={22} fontWeight={600} ml={4}>
        Daily usage (SR Token)
      </TextView>

      {showSkeleton ? (
        <Box sx={{ px: 4 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" width={120} height={56} />
            ))}
          </Box>
          <Skeleton variant="rounded" height={300} sx={{ mt: 3 }} />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mx: 4, mt: 2 }}>
            {visibleSegments.map((tile) => (
              <Box
                key={tile.key}
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 1,
                  borderLeft: `4px solid ${tile.color}`,
                  px: 2,
                  py: 1,
                  minWidth: 120
                }}
              >
                <TextView fontSize={12} fontWeight={500}>
                  {tile.label}
                </TextView>
                <TextView fontSize={18} fontWeight={700}>
                  {(totals[tile.key] ?? 0).toFixed(2)}
                </TextView>
              </Box>
            ))}
          </Box>

          {/* Sits with the tiles rather than in the page header, so a client
              reading "Negotiator" or "Enquiries" for the first time has the
              answer in front of them. The last sentence pre-empts the obvious
              support question when a channel they recognise isn't listed. */}
          <TextView $fontSize={12} $fontWeight={400} $fontColor={'#555'} mx={4} mt={1.5}>
            Each tile shows the SR Tokens charged for that channel this month. Response, Sentiment
            and Enquiries are AI processing; WhatsApp and Twilio cover messaging and calls;
            Negotiator covers CRM activity from the browser extension. Channels with no usage this
            month are not shown.
          </TextView>

          <Box mt={2} />
          <ResponsiveContainer width="100%" height={320}>
            <BarChart
              data={values}
              margin={{
                top: 20,
                right: 20,
                left: 0,
                bottom: 5
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis interval={0} dataKey="date" tick={(props) => CustomizedAxisTick(props)} />
              <YAxis tick={(props) => CustomizedYAxisTick(props)} />
              <Tooltip
                content={
                  <ChartTooltip
                    nameMap={nameMap}
                    valueFormatter={(v) => Number(v).toFixed(2)}
                    showTotal
                    filterZero
                    labelFormatter={(label) => {
                      const dt = DateTime.fromISO(String(label));
                      return dt.isValid ? dt.toFormat('ccc, dd LLL yyyy') : String(label);
                    }}
                  />
                }
                cursor={{ fill: 'rgba(43, 34, 88, 0.06)' }}
              />
              <Legend
                formatter={(value) => SEGMENTS.find((s) => s.key === value)?.label ?? value}
              />
              {visibleSegments.map((segment, idx) => (
                <Bar
                  key={segment.key}
                  dataKey={segment.key}
                  stackId="a"
                  fill={segment.color}
                  // Total label rides the topmost *visible* segment, not the
                  // last of SEGMENTS — that one may now be filtered out.
                  label={idx === visibleSegments.length - 1 ? renderTotalLabel : undefined}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </>
      )}
    </Box>
  );
};

export default SrtUsageChart;
